import { Component, OnDestroy, OnInit } from '@angular/core';
import { ICashRegister } from '../../core/models/cash-register.model';
import { DynamicTableComponent } from '../../shared/components/dynamic-table/dynamic-table.component';
import { getDefaultPageable, Pageable, PageParams } from '../../core/models/page.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CashRegisterService } from '../../core/services/cash-register.service';
import { ToastService } from '../../core/services/toast.service';
import {customConfirmationDialog, customDynamicDialog, DEFAULT_ROWS_PER_PAGE_OPTIONS} from '../../core/utils/utils';
import { CustomDialogData } from '../../core/models/custom-dialog.model';
import { take } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { CashRegisterDetailsComponent } from './cash-register-details/cash-register-details.component';
import { ToolbarComponent } from '../../shared/components/toolbar/toolbar.component';
import { CustomAction, CustomTableHeader } from '../../core/models/custom-table.model';
import {Button} from "primeng/button";
import {CardModule} from "primeng/card";
import {SelectModule} from "primeng/select";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ConfirmationService, PrimeTemplate} from "primeng/api";
import {TooltipModule} from "primeng/tooltip";
import {NgIf} from "@angular/common";
import {IInstitution} from "../../core/models/institution.model";
import {InstitutionService} from "../../core/services/institution.service";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {environment} from "../../../environments/environment.development";

@Component({
    selector: 'app-cash-register',
    imports: [
        DynamicTableComponent,
        ToolbarComponent,
        Button,
        CardModule,
        SelectModule,
        FormsModule,
        PrimeTemplate,
        ReactiveFormsModule,
        TooltipModule,
        NgIf,
        ProgressSpinnerModule
    ],
    providers: [DialogService],
    templateUrl: './cash-register.component.html',
    styleUrls: ['./cash-register.component.scss']
})
export class CashRegisterComponent implements OnInit, OnDestroy {

  cashRegisters: ICashRegister[] = [];
  columns: CustomTableHeader[] = [
    {
      key: 'institutionName',
      column: 'Institution',
      type: 'text'
    },
    {
      key: 'code',
      column: 'Code',
      type: 'text'
    },
    {
      key: 'name',
      column: 'Nom',
      type: 'text'
    },
    {
      key: 'type',
      column: 'Type',
      type: 'text'
    },
    {
      key: 'mainCurrency.code',
      column: 'Devise',
      type: 'text'
    },
    {
      key: 'status',
      column: 'Statut',
      type: 'text'
    },
    {
      key: 'minBalance',
      column: 'Solde Min',
      type: 'number'
    },
    {
      key: 'maxBalance',
      column: 'Solde Max',
      type: 'number'
    }
  ]
  pageable: PageParams = { page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: [] };
  ref: DynamicDialogRef | null = null;
  actions: CustomAction[] = [];
  toolbarActions: CustomAction[] = [];

  institutions : IInstitution[] = []
  searchForm!: FormGroup
  cashRegistersToDisplay: ICashRegister[] = [];
  hasSearched = false
  displaySpinner = false
  production = environment.production;
  cashRegistersType = [
    {type: "Principal"},
    {type: "Secondaire"},
    {type: "Petite caisse"},
    {type : "Change"}
  ]
  cashRegistersStatus = [
    {status: "Active"},
    {status: "Fermée"},
    {status : "Maintenance"}
  ]


  constructor(
    private readonly cashRegisterService: CashRegisterService,
    private readonly toastService: ToastService,
    private readonly dialogService: DialogService,
    private formBuilder: FormBuilder,
    private readonly institutionService: InstitutionService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.initForm();
    this.toolbarActions = [
      {
        role: 'CREATE_CASH',
        icon: 'pi pi-plus',
        label: 'Nouvelle caisse',
        severity: 'primary',
        emit: () => this.createCashRegister(),
        visible: true
      }
    ];
    this.actions = this.getActions();
    this.loadInstitutions();
  }

  initForm() {
    this.searchForm = this.formBuilder.group({
      institution: null,
      type: null,
      status: null
    })
  }

  loadInstitutions(pageable = getDefaultPageable()) {
    pageable.unpaged = true
    this.institutionService.getAllInstitutions(pageable).subscribe({
      next: page => {
        this.institutions = page.content
      },
      error: err => {
        console.log(err)
      }
    })
  }

  onSearch(pageable = getDefaultPageable()) {
    this.hasSearched = true
    this.displaySpinner = true
    this.cashRegisterService.searchCashRegisterByMultipleCriteria(this.searchForm.value, pageable).subscribe({
      next: page => {
        this.cashRegistersToDisplay = page.content
        this.displaySpinner = false
        if (!this.production) console.log(page.content);
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        }
      },
      error: err => {
        if (!this.production) console.log(err)
        this.displaySpinner = false
      }
    })
  }

  onReset() {
    this.searchForm.reset()
    this.cashRegistersToDisplay = []
    this.hasSearched = false
  }
  ngOnDestroy() {
    this.ref?.close();
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'READ_CASH',
        icon: 'pi pi-info-circle',
        label: 'Consulter',
        severity: 'info',
        emit: (item) => this.viewOrEditCashRegister(item),
        visible: true
      },
      {
        role: 'UPDATE_CASH',
        icon: 'pi pi-pen-to-square',
        label: 'Modifier',
        severity: 'primary',
        emit: (item) => this.viewOrEditCashRegister(item, true),
        visible: true
      },
      {
        role: 'UPDATE_CASH',
        icon: 'pi pi-check-circle',
        label: 'Activer',
        severity: 'success',
        emit: (item) => this.activateCashRegister(item),
        visible: (item: ICashRegister) => item.status !== 'Active'
      },
      {
        role: 'UPDATE_CASH',
        icon: 'pi pi-times-circle',
        label: 'Fermer',
        severity: 'warn',
        emit: (item) => this.closeCashRegister(item),
        visible: (item: ICashRegister) => item.status === 'Active'
      },
      {
        role: 'UPDATE_CASH',
        icon: 'pi pi-wrench',
        label: 'Maintenance',
        severity: 'warn',
        emit: (item) => this.setMaintenanceMode(item),
        visible: (item: ICashRegister) => item.status !== 'Maintenance'
      },
      {
        role: 'DELETE_CASH',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item: ICashRegister) => this.confirmDeletion(item),
        visible: true
      }
    ];
  }

  loadCashRegisters(doRefresh = false, pageable: Pageable = getDefaultPageable()) {
    this.cashRegisterService.searchCashRegisterByMultipleCriteria(this.searchForm.value, pageable).subscribe({
      next: page => {
        this.cashRegisters = page.content;
        if (doRefresh)
          this.cashRegistersToDisplay = this.cashRegisters
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        }
      },
      error: err => {
        this.toastService.showToast('Erreur lors du chargement des caisses', err.error, 'error');
      }
    });
  }

  createCashRegister() {
    const data: CustomDialogData<ICashRegister> = {
      data: {} as ICashRegister,
      isCreate: true
    };
    this.ref = this.dialogService.open(CashRegisterDetailsComponent, customDynamicDialog('Créer une caisse', data));
    this.ref?.onClose.pipe(take(1)).subscribe((result: boolean) => {
      if (result) {
        this.loadCashRegisters();
      }
    });
  }

  viewOrEditCashRegister(cashRegister: ICashRegister, isEdit: boolean = false) {
    const data: CustomDialogData<ICashRegister> = {
      data: cashRegister,
      isEdit: isEdit,
      isView: !isEdit
    };
    this.ref = this.dialogService.open(CashRegisterDetailsComponent, customDynamicDialog(isEdit ? 'Modifier la caisse' : 'Détails de la caisse', data));
    this.ref?.onClose.pipe(take(1)).subscribe((result: boolean) => {
      if (result) {
        this.loadCashRegisters();
      }
    });
  }

  activateCashRegister(cashRegister: ICashRegister) {
    if (cashRegister.id) {
      this.cashRegisterService.activateCashRegister(cashRegister.id).subscribe({
        next: () => {
          this.toastService.showToast('Succès', 'Caisse activée avec succès', 'success');
          this.loadCashRegisters();
        },
        error: err => {
          this.toastService.showToast('Erreur', err.error, 'error');
        }
      });
    }
  }

  closeCashRegister(cashRegister: ICashRegister) {
    if (cashRegister.id) {
      this.cashRegisterService.closeCashRegister(cashRegister.id).subscribe({
        next: () => {
          this.toastService.showToast('Succès', 'Caisse fermée avec succès', 'success');
          this.loadCashRegisters();
        },
        error: err => {
          this.toastService.showToast('Erreur', err.error, 'error');
        }
      });
    }
  }

  setMaintenanceMode(cashRegister: ICashRegister) {
    if (cashRegister.id) {
      this.cashRegisterService.setMaintenanceMode(cashRegister.id).subscribe({
        next: () => {
          this.toastService.showToast('Succès', 'Caisse mise en maintenance', 'success');
          this.loadCashRegisters();
        },
        error: err => {
          this.toastService.showToast('Erreur', err.error, 'error');
        }
      });
    }
  }

  confirmDeletion(cashRegister: ICashRegister) {
    const header = 'Suppression d\'une caisse';
    const message = `Êtes-vous sûr de vouloir supprimer la caisse << <strong> ${cashRegister.name}</strong> >> ?`;
    const options = customConfirmationDialog(header, message, { accept: () => this.deleteCashRegister(cashRegister) })
    this.confirmationService.confirm(options)
  }

  deleteCashRegister(cashRegister: ICashRegister) {
    if (cashRegister.id) {
      this.cashRegisterService.deleteCashRegister(cashRegister.id).subscribe({
        next: () => {
          this.toastService.showToast('Succès', 'Caisse supprimée avec succès', 'success');
          this.loadCashRegisters(true);
        },
        error: err => {
          this.toastService.showToast('Erreur', err.error, 'error');
        }
      });
    }
  }

  onPageChange(event: PaginatorState) {
    const pageable: Pageable = {
      page: event.page || 0,
      limit: event.rows || 10,
      sort: '',
      offset: 0,
      paged: true,
      unpaged: false
    };
    this.loadCashRegisters(true, pageable);
  }
}
